import { notify } from '@affine/component';
import { WorkspaceApiError } from '@affine/core/modules/cloud';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { I18n, i18nTime } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import type {
  ConnectionHealth,
  ConnectionPage,
} from '../../../../../modules/streamlain/connections/types';
import {
  isPreparedDemoSample,
  replacesDemoUserFiles,
  showDemoReplacementToast,
} from '../../../../../modules/workspace/utils/demo-input-policy';
import { requestWorkspaceSetting } from '../shared/workspace-settings-api';
import { signatureAppearanceRequest } from './signature-appearance-request';

export type SignatureAppearanceType =
  | 'personal_signature'
  | 'drawn_signature'
  | 'organization_seal';
export type SignatureAppearanceStatus = 'active' | 'disabled';
export type SignatureRole = 'Owner' | 'Manager' | 'Signer' | 'Viewer';
/** Why the signing service of a connection refused a request. */
export type SignatureProviderConnectErrorCode =
  | 'unauthorized'
  | 'unreachable'
  | 'per_signature_approval'
  | 'pin_rejected'
  | 'insecure_url'
  | 'bad_response';
/** How the provider releases the key of a certificate for one signature. */
export type SignatureCertificateKeyRelease = 'application' | 'pin';
/** Where the fixed PIN of a certificate stands; the PIN stays on the server. */
export type SignatureCertificatePinState =
  | 'not_needed'
  | 'missing'
  | 'stored'
  | 'rejected';
export type SignatureCertificateStatus =
  | 'active'
  | 'disabled'
  | 'expired'
  | 'revoked'
  | 'untrusted';
export type SignatureSigningProfileStatus = 'active' | 'disabled';
export type SignatureSigningProfileReadinessStatus =
  | 'not_configured'
  | 'auth_required'
  | 'identity_pending'
  | 'certificate_ready'
  | 'test_sign_passed'
  | 'expired'
  | 'revoked'
  | 'disabled'
  | 'provider_outage';
export type SignatureTestSignStatus =
  | 'not_run'
  | 'running'
  | 'passed'
  | 'failed';
export type PadesLevel = 'B' | 'T' | 'LT' | 'LTA';
export type PdfSignatureAnnotationMode = 'none' | 'flattened';
export type PdfSignatureSourceKind = 'original_pdf' | 'flattened_annotations';
export type PdfSigningOutputMode = 'download';
export type PdfSigningJobStatus =
  | 'queued'
  | 'running'
  | 'finalizing'
  | 'completed'
  | 'failed'
  | 'cancelled';
export type SignatureValidationState =
  | 'valid'
  | 'invalid'
  | 'unknown'
  | 'untrusted'
  | 'expired'
  | 'revoked'
  | 'visual_only';

export interface SignatureAppearanceInfo {
  id: string;
  workspaceId: string;
  ownerUserId: string;
  ownerDisplayName: string;
  ownerEmail: string;
  ownerAvatarUrl: string | null;
  name: string;
  type: SignatureAppearanceType;
  blobKey: string;
  mimeType: 'image/png' | 'image/jpeg' | 'image/svg+xml';
  width: number;
  height: number;
  status: SignatureAppearanceStatus;
  isDefault: boolean;
  canUse: boolean;
  canManage: boolean;
  role: SignatureRole | null;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureRoleEntry {
  id: string;
  type: 'user' | 'group';
  targetId: string;
  targetName: string;
  targetEmail?: string;
  targetAvatarUrl?: string | null;
  role: SignatureRole;
  createdAt: string;
}

/** Workspace connections of this service sign PDFs. */
export const SIGNATURE_CONNECTION_SERVICE_SLUG = 'csc_signature';

/**
 * A workspace connection of the signature service (Arbeitsbereich ›
 * Verbindungen). Admins set it up there; signing reads it from there.
 */
export interface SignatureConnectionInfo {
  id: string;
  name: string;
  health: ConnectionHealth;
}

export interface SignatureCertificateRefInfo {
  id: string;
  workspaceId: string;
  /** The signature connection that offers the certificate. */
  connectionId: string;
  name: string;
  subject: string | null;
  issuer: string | null;
  fingerprint: string;
  serialNumber: string | null;
  expiresAt: string | null;
  status: SignatureCertificateStatus;
  keyRelease: SignatureCertificateKeyRelease;
  pinState: SignatureCertificatePinState;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureWorkspacePolicyInfo {
  id: string;
  workspaceId: string;
  /** Signature connections members may sign with; empty allows all. */
  allowedConnectionIds: string[];
  allowVisualOnlyExport: boolean;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SignatureSigningProfileInfo {
  id: string;
  workspaceId: string;
  ownerUserId: string;
  connectionId: string;
  certificateRefId: string;
  name: string;
  status: SignatureSigningProfileStatus;
  readinessStatus: SignatureSigningProfileReadinessStatus;
  testSignStatus: SignatureTestSignStatus;
  testSignValidationState: SignatureValidationState | null;
  failureReason: string | null;
  isDefault: boolean;
  connectionName: string | null;
  certificateName: string | null;
  certificateSubject: string | null;
  certificateIssuer: string | null;
  certificateFingerprint: string | null;
  lastTestSignAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PdfSignaturePlacementSnapshot {
  id: string;
  signatureAppearanceId?: string;
  sourceId: string;
  pageIndex: number;
  rect: { x: number; y: number; width: number; height: number };
  rotation: number;
  createdBy?: string;
  createdAt?: number;
}

export interface PdfSigningJobInfo {
  id: string;
  workspaceId: string;
  requestedByUserId: string;
  sourceBlobKey: string;
  sourceHash: string;
  signingSourceBlobKey: string | null;
  signingSourceHash: string | null;
  sourceKind: PdfSignatureSourceKind;
  annotationMode: PdfSignatureAnnotationMode;
  docId: string | null;
  attachmentBlockId: string | null;
  appearanceAssetId: string | null;
  placementSnapshot: PdfSignaturePlacementSnapshot | null;
  signingProfileId: string | null;
  signingProfileName: string | null;
  /**
   * Empty once the signature connection was removed; `providerName`, the
   * connection's name at signing time, stays.
   */
  connectionId: string | null;
  providerName: string | null;
  certificateRefId: string | null;
  certificateSubject: string | null;
  certificateIssuer: string | null;
  certificateFingerprint: string | null;
  padesLevel: PadesLevel;
  outputMode: PdfSigningOutputMode;
  fileName: string | null;
  status: PdfSigningJobStatus;
  failureReason: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface PdfSigningSourceSnapshotInfo {
  id: string;
  workspaceId: string;
  sourceBlobKey: string;
  sourceHash: string;
  signingSourceBlobKey: string;
  signingSourceHash: string;
  docId: string;
  attachmentBlockId: string;
  createdByUserId: string;
  expiresAt: string;
  createdAt: string;
}

export interface SignedPdfVersionInfo {
  id: string;
  workspaceId: string;
  signingJobId: string | null;
  sourceBlobKey: string;
  sourceHash: string;
  signingSourceBlobKey: string | null;
  signingSourceHash: string | null;
  sourceKind: PdfSignatureSourceKind;
  annotationMode: PdfSignatureAnnotationMode;
  signedBlobKey: string;
  signedHash: string;
  fileName: string | null;
  padesLevel: PadesLevel;
  appearanceAssetId: string | null;
  signingProfileId: string | null;
  signingProfileName: string | null;
  connectionId: string | null;
  providerName: string | null;
  certificateRefId: string | null;
  certificateSubject: string | null;
  certificateIssuer: string | null;
  certificateFingerprint: string | null;
  validationState: SignatureValidationState;
  validationSummary: Record<string, unknown> | null;
  createdByUserId: string;
  createdAt: string;
}

export type PdfSignatureErrorAction =
  | 'cancel'
  | 'export'
  | 'load'
  | 'save'
  | 'sign'
  | 'upload';
export type PdfSignatureErrorKind =
  | 'conflict'
  | 'invalid'
  | 'network'
  | 'notFound'
  | 'permission'
  | 'plan'
  | 'quota'
  | 'timeout'
  | 'tooLarge'
  | 'unknown';

export class PdfSignatureRequestError extends Error {
  readonly originalError?: unknown;
  readonly responseMessage?: string;
  readonly responseCode?: string;
  /** The server's answer as sent, e.g. the limit and reset of a quota. */
  readonly responseBody?: unknown;
  readonly status?: number;

  constructor(input: {
    originalError?: unknown;
    responseMessage?: string;
    responseCode?: string;
    responseBody?: unknown;
    status?: number;
  }) {
    super(I18n.t('com.streamlain.pdf.signature.error.action.load'));
    this.name = 'PdfSignatureRequestError';
    this.originalError = input.originalError;
    this.responseMessage = input.responseMessage;
    this.responseCode = input.responseCode;
    this.responseBody = input.responseBody;
    this.status = input.status;
  }
}

const SIGNATURE_PROVIDER_CONNECT_ERROR_CODES: ReadonlySet<string> =
  new Set<SignatureProviderConnectErrorCode>([
    'unauthorized',
    'unreachable',
    'per_signature_approval',
    'pin_rejected',
    'insecure_url',
    'bad_response',
  ]);

/** The reason the provider gave, if the request failed at the provider. */
export function getSignatureProviderConnectErrorCode(
  error: unknown
): SignatureProviderConnectErrorCode | null {
  if (!(error instanceof PdfSignatureRequestError)) return null;
  const code = error.responseCode;
  return code && SIGNATURE_PROVIDER_CONNECT_ERROR_CODES.has(code)
    ? (code as SignatureProviderConnectErrorCode)
    : null;
}

const PDF_SIGNATURE_ERROR_ACTION_KEYS = {
  cancel: 'com.streamlain.pdf.signature.error.action.cancel',
  export: 'com.streamlain.pdf.signature.error.action.export',
  load: 'com.streamlain.pdf.signature.error.action.load',
  save: 'com.streamlain.pdf.signature.error.action.save',
  sign: 'com.streamlain.pdf.signature.error.action.sign',
  upload: 'com.streamlain.pdf.signature.error.action.upload',
} as const;

const PDF_SIGNATURE_ERROR_CAUSE_KEYS = {
  conflict: 'com.streamlain.pdf.signature.error.cause.conflict',
  invalid: 'com.streamlain.pdf.signature.error.cause.invalid',
  network: 'com.streamlain.pdf.signature.error.cause.network',
  notFound: 'com.streamlain.pdf.signature.error.cause.notFound',
  permission: 'com.streamlain.pdf.signature.error.cause.permission',
  plan: 'com.streamlain.pdf.signature.error.cause.plan',
  quota: 'com.streamlain.pdf.signature.error.cause.quota',
  timeout: 'com.streamlain.pdf.signature.error.cause.timeout',
  tooLarge: 'com.streamlain.pdf.signature.error.cause.tooLarge',
  unknown: 'com.streamlain.pdf.signature.error.cause.unknown',
} as const;

function stringifyPdfSignatureError(error: unknown): string {
  if (error instanceof PdfSignatureRequestError) {
    return [
      error.message,
      error.responseMessage,
      error.originalError instanceof Error
        ? error.originalError.message
        : String(error.originalError ?? ''),
    ]
      .join(' ')
      .toLowerCase();
  }
  if (error instanceof Error) {
    return error.message.toLowerCase();
  }
  return String(error ?? '').toLowerCase();
}

export function getPdfSignatureErrorKind(
  error: unknown
): PdfSignatureErrorKind {
  const status =
    error instanceof PdfSignatureRequestError ? error.status : undefined;
  // The digital signatures of the plan for this month are used up.
  if (
    error instanceof PdfSignatureRequestError &&
    error.responseCode === PDF_DIGITAL_SIGNATURE_MONTHLY_LIMIT_CODE
  ) {
    return 'quota';
  }
  // The server's feature guard: the workspace plan lacks the digital signature.
  if (
    error instanceof PdfSignatureRequestError &&
    error.responseCode === 'FEATURE_NOT_AVAILABLE'
  ) {
    return 'plan';
  }
  if (status === 401 || status === 403) return 'permission';
  if (status === 404) return 'notFound';
  if (status === 413) return 'tooLarge';
  if (status === 400 || status === 415) return 'invalid';
  if (status === 409 || status === 422) return 'conflict';
  if (status === 408 || status === 504) return 'timeout';
  if (status === 502 || status === 503) return 'network';

  const message = stringifyPdfSignatureError(error);
  if (/\b(timeout|timed out|gateway timeout)\b/.test(message)) {
    return 'timeout';
  }
  if (
    /\b(failed to fetch|load failed|network|proxy|econnrefused|server unavailable|service unavailable)\b/.test(
      message
    )
  ) {
    return 'network';
  }
  if (/\b(forbidden|unauthorized|permission)\b/.test(message)) {
    return 'permission';
  }
  if (/\b(not found|missing)\b/.test(message)) {
    return 'notFound';
  }
  if (/\b(payload too large|file too large|too large)\b/.test(message)) {
    return 'tooLarge';
  }
  if (/\b(invalid|unsupported|bad request)\b/.test(message)) {
    return 'invalid';
  }
  if (/\b(conflict|not ready|locked)\b/.test(message)) {
    return 'conflict';
  }
  return 'unknown';
}

export function getPdfSignatureErrorMessage(
  error: unknown,
  action: PdfSignatureErrorAction
): string {
  const actionText = I18n.t(PDF_SIGNATURE_ERROR_ACTION_KEYS[action]);
  const kind = getPdfSignatureErrorKind(error);
  const causeText =
    kind === 'quota'
      ? I18n.t(PDF_SIGNATURE_ERROR_CAUSE_KEYS.quota, {
          date: formatQuotaResetDate(quotaResetsAt(error)),
        })
      : I18n.t(PDF_SIGNATURE_ERROR_CAUSE_KEYS[kind]);
  return `${actionText} ${causeText}`;
}

/**
 * The error toast of a signature action. A refusal for the plan or its
 * monthly quota offers the plans when the viewer may open them.
 */
export function notifyPdfSignatureError(
  error: unknown,
  action: PdfSignatureErrorAction,
  openPlans: (() => void) | null = null
): void {
  const kind = getPdfSignatureErrorKind(error);
  notify.error({
    title: getPdfSignatureErrorMessage(error, action),
    actions:
      openPlans && (kind === 'quota' || kind === 'plan')
        ? [
            {
              key: 'open-plans',
              label: I18n.t('com.streamlain.payment.view-plans'),
              onClick: openPlans,
            },
          ]
        : undefined,
  });
}

/** Code of a request refused at the plan's monthly digital signatures. */
export const PDF_DIGITAL_SIGNATURE_MONTHLY_LIMIT_CODE =
  'PDF_DIGITAL_SIGNATURE_MONTHLY_LIMIT';

/** When the monthly digital signatures start again, from the refusal. */
function quotaResetsAt(error: unknown): string {
  const body =
    error instanceof PdfSignatureRequestError ? error.responseBody : null;
  const resetsAt =
    body && typeof body === 'object'
      ? (body as { resetsAt?: unknown }).resetsAt
      : undefined;
  return typeof resetsAt === 'string' ? resetsAt : '';
}

/** The day a monthly quota starts again, in the user's language. */
export function formatQuotaResetDate(resetsAt: string): string {
  return i18nTime(resetsAt, { absolute: { accuracy: 'day' } });
}

/**
 * A failed request as the settings and the viewer read it. The workspace
 * client refuses every answer that is not a success, so the status and the
 * reason of the server arrive here on its error.
 */
export function toPdfSignatureRequestError(
  error: unknown
): PdfSignatureRequestError {
  if (error instanceof WorkspaceApiError) {
    return new PdfSignatureRequestError({
      originalError: error,
      responseMessage: error.message,
      responseCode: error.code,
      responseBody: error.responseBody,
      status: error.status,
    });
  }
  return new PdfSignatureRequestError({ originalError: error });
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await requestWorkspaceSetting(workspaceIdFromApiPath(url), url, init);
  } catch (error) {
    throw toPdfSignatureRequestError(error);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

function workspaceIdFromApiPath(url: string): string {
  const match = /^\/api\/workspace\/([^/]+)/.exec(url);
  if (!match) {
    throw new PdfSignatureRequestError({
      responseMessage: 'PDF signature request is missing workspace scope',
    });
  }
  return decodeURIComponent(match[1]);
}

type PdfSignatureLoadState<T> = {
  data: T;
  error: unknown | null;
};

/**
 * The signature connections of the workspace, read from its connections.
 * Connections of a service the plan lacks are not listed.
 */
async function fetchSignatureConnections(
  url: string
): Promise<PdfSignatureLoadState<{ connections: SignatureConnectionInfo[] }>> {
  const connections: SignatureConnectionInfo[] = [];
  let cursor: string | null = null;
  try {
    do {
      const page: ConnectionPage = await fetchJson<ConnectionPage>(
        cursor ? `${url}?cursor=${encodeURIComponent(cursor)}` : url
      );
      for (const item of page.items) {
        if (item.serviceDefinition.slug !== SIGNATURE_CONNECTION_SERVICE_SLUG) {
          continue;
        }
        connections.push({
          id: item.id,
          name: item.label,
          health: item.healthStatus,
        });
      }
      cursor = page.nextCursor;
    } while (cursor);
    return { data: { connections }, error: null };
  } catch (error) {
    return { data: { connections: [] }, error };
  }
}

async function fetchPdfSignatureLoadJson<T>(
  url: string,
  fallback: T
): Promise<PdfSignatureLoadState<T>> {
  try {
    return {
      data: await fetchJson<T>(url),
      error: null,
    };
  } catch (error) {
    return {
      data: fallback,
      error,
    };
  }
}

export function useSignedPdfVersionForBlob(
  signedBlobKey?: string | null,
  enabled = true
) {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const apiBase = `/api/workspace/${workspaceId}/pdf-signatures`;
  const signedVersion = useSWRImmutable<
    PdfSignatureLoadState<{ versions: SignedPdfVersionInfo[] }>
  >(
    enabled && signedBlobKey
      ? `${apiBase}/signed-versions?signedBlobKey=${encodeURIComponent(
          signedBlobKey
        )}`
      : null,
    url => fetchPdfSignatureLoadJson(url, { versions: [] }),
    { suspense: false }
  );
  const signedVersionDownloadUrl = useCallback(
    (versionId: string) =>
      `${apiBase}/signed-versions/${encodeURIComponent(versionId)}/download`,
    [apiBase]
  );

  return useMemo(
    () => ({
      signedVersion: signedVersion.data?.data.versions[0] ?? null,
      error: signedVersion.data?.error ?? null,
      isLoading: Boolean(enabled && signedBlobKey && !signedVersion.data),
      refresh: signedVersion.mutate,
      signedVersionDownloadUrl,
    }),
    [
      enabled,
      signedBlobKey,
      signedVersion.data,
      signedVersion.mutate,
      signedVersionDownloadUrl,
    ]
  );
}

/** The digital signatures of the current UTC month against the plan. */
export interface PdfDigitalSignatureUsageInfo {
  used: number;
  /** Places held by running jobs: they become a signature or free up. */
  inflight: number;
  /** -1 = unlimited. */
  limit: number;
  periodStart: string;
  resetsAt: string;
}

/**
 * The monthly digital signatures of the workspace. Loads again whenever a
 * view of them mounts, so a page shows the signatures made since.
 */
export function usePdfDigitalSignatureUsage(enabled = true) {
  const workspaceId = useService(WorkspaceService).workspace.id;
  const usage = useSWR<
    PdfSignatureLoadState<{ usage: PdfDigitalSignatureUsageInfo | null }>
  >(
    enabled ? `/api/workspace/${workspaceId}/pdf-signatures/usage` : null,
    url => fetchPdfSignatureLoadJson(url, { usage: null }),
    CONNECTION_BOUND_SWR
  );
  return {
    usage: usage.data?.data.usage ?? null,
    error: usage.data?.error ?? null,
    isLoading: Boolean(enabled && !usage.data),
  };
}

const CONNECTION_BOUND_SWR = {
  suspense: false,
  revalidateOnMount: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
} as const;

export function useWorkspacePdfSignatures(
  sourceBlobKey?: string | null,
  enabled = true
) {
  const workspaceService = useService(WorkspaceService);
  const workspaceId = workspaceService.workspace.id;
  const [isSaving, setIsSaving] = useState(false);
  const apiBase = `/api/workspace/${workspaceId}/pdf-signatures`;
  const sourceQuery = sourceBlobKey
    ? `?sourceBlobKey=${encodeURIComponent(sourceBlobKey)}`
    : '';

  const appearances = useSWRImmutable<
    PdfSignatureLoadState<{ appearances: SignatureAppearanceInfo[] }>
  >(
    enabled ? `${apiBase}/appearances` : null,
    url => fetchPdfSignatureLoadJson(url, { appearances: [] }),
    { suspense: false }
  );
  // Connections are set up, renewed and removed under Credentials; removing
  // one takes its certificates and profiles along. These three load again
  // whenever a view of them mounts, so no page shows a removed connection.
  const connections = useSWR<
    PdfSignatureLoadState<{ connections: SignatureConnectionInfo[] }>
  >(
    enabled ? `/api/workspace/${workspaceId}/connections` : null,
    fetchSignatureConnections,
    CONNECTION_BOUND_SWR
  );
  const certificates = useSWR<
    PdfSignatureLoadState<{ certificates: SignatureCertificateRefInfo[] }>
  >(
    enabled ? `${apiBase}/certificates` : null,
    url => fetchPdfSignatureLoadJson(url, { certificates: [] }),
    CONNECTION_BOUND_SWR
  );
  const profiles = useSWR<
    PdfSignatureLoadState<{ profiles: SignatureSigningProfileInfo[] }>
  >(
    enabled ? `${apiBase}/profiles` : null,
    url => fetchPdfSignatureLoadJson(url, { profiles: [] }),
    CONNECTION_BOUND_SWR
  );
  const policy = useSWRImmutable<
    PdfSignatureLoadState<{ policy: SignatureWorkspacePolicyInfo | null }>
  >(
    enabled ? `${apiBase}/policy` : null,
    url => fetchPdfSignatureLoadJson(url, { policy: null }),
    { suspense: false }
  );
  const signedVersions = useSWRImmutable<
    PdfSignatureLoadState<{ versions: SignedPdfVersionInfo[] }>
  >(
    enabled ? `${apiBase}/signed-versions${sourceQuery}` : null,
    url => fetchPdfSignatureLoadJson(url, { versions: [] }),
    { suspense: false }
  );

  const refresh = useCallback(async () => {
    await Promise.all([
      appearances.mutate(),
      connections.mutate(),
      certificates.mutate(),
      profiles.mutate(),
      policy.mutate(),
      signedVersions.mutate(),
    ]);
  }, [
    appearances,
    certificates,
    connections,
    policy,
    profiles,
    signedVersions,
  ]);

  const uploadAppearance = useCallback(
    async (input: {
      name: string;
      type: SignatureAppearanceType;
      file: File;
    }) => {
      setIsSaving(true);
      try {
        const isDemo = await replacesDemoUserFiles(workspaceId);
        const { url, init } = signatureAppearanceRequest(
          apiBase,
          input,
          isDemo,
          String(I18n.language).toLowerCase().startsWith('de') ? 'de' : 'en'
        );
        const result = await fetchJson<{ appearance: SignatureAppearanceInfo }>(
          url,
          init
        );
        if (isDemo && !isPreparedDemoSample(input.file)) {
          showDemoReplacementToast();
        }
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.upload.success'),
        });
        await appearances.mutate();
        return result.appearance;
      } catch (error) {
        notify.error({
          title: getPdfSignatureErrorMessage(error, 'upload'),
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances, workspaceId]
  );

  /** Reads the certificates a signature connection offers and stores them. */
  const refreshCertificates = useCallback(
    async (connection: Pick<SignatureConnectionInfo, 'id' | 'name'>) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          certificates: SignatureCertificateRefInfo[];
        }>(`${apiBase}/certificates/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ connectionId: connection.id }),
        });
        notify.success({
          title: I18n.t(
            'com.streamlain.pdf.signature.toast.certificatesRefreshed',
            {
              name: connection.name,
              count: String(result.certificates.length),
            }
          ),
        });
        return result.certificates;
      } finally {
        // A refused access changes the connection's state on the server too.
        await Promise.all([
          connections.mutate(),
          certificates.mutate(),
          profiles.mutate(),
        ]);
        setIsSaving(false);
      }
    },
    [apiBase, certificates, connections, profiles]
  );

  const listAppearanceRoles = useCallback(
    async (appearanceId: string) => {
      const result = await fetchJson<{ roles: SignatureRoleEntry[] }>(
        `${apiBase}/appearances/${encodeURIComponent(appearanceId)}/roles`
      );
      return result.roles;
    },
    [apiBase]
  );

  const assignAppearanceRole = useCallback(
    async (input: {
      appearanceId: string;
      type: 'user' | 'group';
      targetId: string;
      role: Exclude<SignatureRole, 'Owner'>;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ roles: SignatureRoleEntry[] }>(
          `${apiBase}/appearances/${encodeURIComponent(input.appearanceId)}/roles`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: input.type,
              targetId: input.targetId,
              role: input.role,
            }),
          }
        );
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.toast.accessUpdated'),
        });
        await appearances.mutate();
        return result.roles;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const removeAppearanceRole = useCallback(
    async (appearanceId: string, roleId: string) => {
      setIsSaving(true);
      try {
        await fetchJson<void>(
          `${apiBase}/appearances/${encodeURIComponent(
            appearanceId
          )}/roles/${encodeURIComponent(roleId)}`,
          { method: 'DELETE' }
        );
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.toast.accessRemoved'),
        });
        await appearances.mutate();
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const setDefaultAppearance = useCallback(
    async (appearanceId: string) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ appearance: SignatureAppearanceInfo }>(
          `${apiBase}/appearances/${encodeURIComponent(appearanceId)}/default`,
          { method: 'POST' }
        );
        notify.success({
          title: I18n.t(
            'com.streamlain.pdf.signature.toast.defaultSignatureUpdated'
          ),
        });
        await appearances.mutate();
        return result.appearance;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const updateAppearance = useCallback(
    async (
      appearanceId: string,
      input: {
        name?: string;
        status?: SignatureAppearanceStatus;
      }
    ) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ appearance: SignatureAppearanceInfo }>(
          `${apiBase}/appearances/${encodeURIComponent(appearanceId)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
          }
        );
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.settings.saved'),
        });
        await appearances.mutate();
        return result.appearance;
      } catch (error) {
        notify.error({
          title: getPdfSignatureErrorMessage(error, 'upload'),
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const deleteAppearance = useCallback(
    async (appearanceId: string) => {
      setIsSaving(true);
      try {
        await fetchJson<void>(
          `${apiBase}/appearances/${encodeURIComponent(appearanceId)}`,
          { method: 'DELETE' }
        );
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.settings.deleted'),
        });
        await appearances.mutate();
      } catch (error) {
        notify.error({
          title: getPdfSignatureErrorMessage(error, 'upload'),
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances]
  );

  const updateCertificate = useCallback(
    async (
      certificateRefId: string,
      input: {
        name?: string;
        // Expiry and revocation come from the provider, never from an admin.
        status?: Extract<SignatureCertificateStatus, 'active' | 'disabled'>;
      }
    ) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          certificate: SignatureCertificateRefInfo;
        }>(`${apiBase}/certificates/${encodeURIComponent(certificateRefId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        notify.success({
          title: I18n.t(
            'com.streamlain.pdf.signature.toast.certificateUpdated'
          ),
        });
        await Promise.all([certificates.mutate(), profiles.mutate()]);
        return result.certificate;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, certificates, profiles]
  );

  /** Stores the fixed PIN of a certificate (checked at the provider) or, with `null`, removes it. */
  const setCertificatePin = useCallback(
    async (certificateRefId: string, pin: string | null) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          certificate: SignatureCertificateRefInfo;
        }>(
          `${apiBase}/certificates/${encodeURIComponent(certificateRefId)}/pin`,
          pin === null
            ? { method: 'DELETE' }
            : {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin }),
              }
        );
        notify.success({
          title: I18n.t(
            pin === null
              ? 'com.streamlain.pdf.signature.toast.certificatePinRemoved'
              : 'com.streamlain.pdf.signature.toast.certificatePinStored'
          ),
        });
        await Promise.all([certificates.mutate(), profiles.mutate()]);
        return result.certificate;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, certificates, profiles]
  );

  /**
   * Activation is the provider's own answer to "can this profile sign": a
   * signature over a blank test page. The outcome is told, not the attempt.
   * A caller that shows the good outcome itself passes `announceReady: false`;
   * a failed activation is always reported here.
   */
  const checkSigningProfile = useCallback(
    async (
      profile: SignatureSigningProfileInfo,
      { announceReady = true }: { announceReady?: boolean } = {}
    ) => {
      const notActivated = I18n.t(
        'com.streamlain.pdf.signature.toast.profileNotActivated',
        { name: profile.name }
      );
      try {
        const result = await fetchJson<{
          profile: SignatureSigningProfileInfo;
        }>(`${apiBase}/profiles/${encodeURIComponent(profile.id)}/test-sign`, {
          method: 'POST',
        });
        if (result.profile.readinessStatus === 'test_sign_passed') {
          if (announceReady) {
            notify.success({
              title: I18n.t('com.streamlain.pdf.signature.toast.profileReady', {
                name: result.profile.name,
              }),
            });
          }
        } else {
          notify.error({
            title: notActivated,
            message: result.profile.failureReason ?? undefined,
          });
        }
        return result.profile;
      } catch (error) {
        notify.error({
          title: notActivated,
          message: getPdfSignatureErrorMessage(error, 'save'),
        });
        return profile;
      }
    },
    [apiBase]
  );

  /**
   * Creates a profile and activates it right away. The form that created it
   * confirms a ready profile itself.
   */
  const createSigningProfile = useCallback(
    async (input: {
      name: string;
      connectionId: string;
      certificateRefId: string;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          profile: SignatureSigningProfileInfo;
        }>(`${apiBase}/profiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        return await checkSigningProfile(result.profile, {
          announceReady: false,
        });
      } finally {
        await profiles.mutate();
        setIsSaving(false);
      }
    },
    [apiBase, checkSigningProfile, profiles]
  );

  /** Makes a profile ready to sign; a switched-off profile is switched on first. */
  const activateSigningProfile = useCallback(
    async (profile: SignatureSigningProfileInfo) => {
      setIsSaving(true);
      try {
        const active =
          profile.status === 'active'
            ? profile
            : (
                await fetchJson<{ profile: SignatureSigningProfileInfo }>(
                  `${apiBase}/profiles/${encodeURIComponent(profile.id)}`,
                  {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ status: 'active' }),
                  }
                )
              ).profile;
        return await checkSigningProfile(active);
      } finally {
        await profiles.mutate();
        setIsSaving(false);
      }
    },
    [apiBase, checkSigningProfile, profiles]
  );

  const deleteSigningProfile = useCallback(
    async (profile: Pick<SignatureSigningProfileInfo, 'id' | 'name'>) => {
      setIsSaving(true);
      try {
        await fetchJson<void>(
          `${apiBase}/profiles/${encodeURIComponent(profile.id)}`,
          { method: 'DELETE' }
        );
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.toast.profileDisabled', {
            name: profile.name,
          }),
        });
        await profiles.mutate();
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, profiles]
  );

  const setDefaultSigningProfile = useCallback(
    async (profileId: string) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          profile: SignatureSigningProfileInfo;
        }>(`${apiBase}/profiles/${encodeURIComponent(profileId)}/default`, {
          method: 'POST',
        });
        notify.success({
          title: I18n.t(
            'com.streamlain.pdf.signature.toast.defaultProfileUpdated',
            { name: result.profile.name }
          ),
        });
        await profiles.mutate();
        return result.profile;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, profiles]
  );

  const updatePolicy = useCallback(
    async (input: {
      allowedConnectionIds?: string[];
      allowVisualOnlyExport?: boolean;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{
          policy: SignatureWorkspacePolicyInfo;
        }>(`${apiBase}/policy`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(input),
        });
        notify.success({
          title: I18n.t('com.streamlain.pdf.signature.toast.policySaved'),
        });
        await policy.mutate();
        return result.policy;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, policy]
  );

  const recordVisualExport = useCallback(
    async (input: {
      sourceBlobKey: string;
      docId?: string | null;
      attachmentBlockId?: string | null;
      placementCount: number;
      output: 'print' | 'export';
    }) => {
      await fetchJson<void>(`${apiBase}/visual-exports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
    },
    [apiBase]
  );

  const createSigningSourceSnapshot = useCallback(
    async (input: {
      sourceBlobKey: string;
      signingSourceBlobKey: string;
      docId: string;
      attachmentBlockId: string;
    }) => {
      const result = await fetchJson<{
        snapshot: PdfSigningSourceSnapshotInfo;
      }>(`${apiBase}/signing-source-snapshots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return result.snapshot;
    },
    [apiBase]
  );

  const requestSigning = useCallback(
    async (input: {
      sourceBlobKey: string;
      signingSourceBlobKey?: string | null;
      signingSourceSnapshotId?: string | null;
      includeAnnotations?: boolean;
      docId?: string | null;
      attachmentBlockId?: string | null;
      appearanceAssetId?: string | null;
      placementSnapshot?: PdfSignaturePlacementSnapshot | null;
      profileId: string;
      fileName?: string | null;
    }) => {
      setIsSaving(true);
      try {
        const result = await fetchJson<{ job: PdfSigningJobInfo }>(
          `${apiBase}/signing-jobs`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input),
          }
        );
        await Promise.all([signedVersions.mutate(), appearances.mutate()]);
        return result.job;
      } finally {
        setIsSaving(false);
      }
    },
    [apiBase, appearances, signedVersions]
  );

  const getSigningJob = useCallback(
    async (jobId: string) => {
      const result = await fetchJson<{ job: PdfSigningJobInfo }>(
        `${apiBase}/signing-jobs/${encodeURIComponent(jobId)}`
      );
      return result.job;
    },
    [apiBase]
  );

  const cancelSigningJob = useCallback(
    async (jobId: string) => {
      const result = await fetchJson<{ job: PdfSigningJobInfo }>(
        `${apiBase}/signing-jobs/${encodeURIComponent(jobId)}/cancel`,
        { method: 'POST' }
      );
      await signedVersions.mutate();
      return result.job;
    },
    [apiBase, signedVersions]
  );

  const appearanceImageUrl = useCallback(
    (appearanceId: string) =>
      `${apiBase}/appearances/${encodeURIComponent(appearanceId)}/file`,
    [apiBase]
  );

  const signedVersionDownloadUrl = useCallback(
    (versionId: string) =>
      `${apiBase}/signed-versions/${encodeURIComponent(versionId)}/download`,
    [apiBase]
  );

  return useMemo(
    () => ({
      workspaceId,
      appearances: appearances.data?.data.appearances ?? [],
      connections: connections.data?.data.connections ?? [],
      certificates: certificates.data?.data.certificates ?? [],
      profiles: profiles.data?.data.profiles ?? [],
      policy: policy.data?.data.policy ?? null,
      signedVersions: signedVersions.data?.data.versions ?? [],
      loadError:
        appearances.data?.error ??
        connections.data?.error ??
        certificates.data?.error ??
        profiles.data?.error ??
        policy.data?.error ??
        signedVersions.data?.error ??
        null,
      isLoading:
        enabled &&
        (!appearances.data ||
          !connections.data ||
          !certificates.data ||
          !profiles.data ||
          !policy.data),
      isSaving,
      refresh,
      uploadAppearance,
      refreshCertificates,
      listAppearanceRoles,
      assignAppearanceRole,
      removeAppearanceRole,
      setDefaultAppearance,
      updateAppearance,
      deleteAppearance,
      updateCertificate,
      setCertificatePin,
      createSigningProfile,
      activateSigningProfile,
      deleteSigningProfile,
      setDefaultSigningProfile,
      updatePolicy,
      recordVisualExport,
      createSigningSourceSnapshot,
      requestSigning,
      getSigningJob,
      cancelSigningJob,
      appearanceImageUrl,
      signedVersionDownloadUrl,
    }),
    [
      appearanceImageUrl,
      assignAppearanceRole,
      appearances.data,
      certificates.data,
      cancelSigningJob,
      connections.data,
      createSigningSourceSnapshot,
      createSigningProfile,
      deleteSigningProfile,
      getSigningJob,
      isSaving,
      listAppearanceRoles,
      policy.data,
      profiles.data,
      recordVisualExport,
      refresh,
      refreshCertificates,
      removeAppearanceRole,
      requestSigning,
      setDefaultAppearance,
      setDefaultSigningProfile,
      signedVersionDownloadUrl,
      signedVersions.data,
      deleteAppearance,
      updateCertificate,
      setCertificatePin,
      updateAppearance,
      activateSigningProfile,
      updatePolicy,
      uploadAppearance,
      workspaceId,
      enabled,
    ]
  );
}
